<template>
  <!--
    Info panel for the supplier payment capture screen: where the owner's account stands,
    what this batch will take out of it, and what is left afterwards. The same shape as the
    cashbook panel, because it answers the same question about a different account.

    Fed by query set group "Supplier Payment Owner Balance Queries" (meta.query
    SupplierPaymentOwnerBalance), parameterised on SupplierPaymentHeaderId from the form
    record context.

    One row per owner in the batch. In practice a batch is one owner's, but nothing
    enforces that, and showing the first of two silently would be worse than showing both.
  -->
  <div v-if="owners.length" class="rounded border border-gray-200 p-3 text-sm">
    <div v-for="(o, i) in owners" :key="o.owner_id ?? i" :class="i ? 'mt-3 pt-3 border-t border-gray-200' : ''">
      <div class="font-semibold mb-2">
        {{ o.owner_name }}
        <span v-if="o.owner_id" class="font-normal text-gray-500">({{ o.owner_id }})</span>
        <span v-if="o.batch_status" class="ml-2 font-normal text-gray-400">
          &middot; {{ o.batch_status }}
        </span>
      </div>

      <div class="grid grid-cols-3 gap-3">
        <div>
          <div class="text-gray-500">Owner balance</div>
          <div class="font-semibold">{{ money(o.owner_balance) }}</div>
        </div>
        <div>
          <div class="text-gray-500">
            This batch
            <span v-if="o.line_count != null" class="text-gray-400">
              ({{ o.line_count }})
            </span>
          </div>
          <div class="font-semibold">{{ money(o.batch_total) }}</div>
        </div>
        <div>
          <div class="text-gray-500">
            After posting
            <!--
              Subtracts this batch AND the owner's draft payments in other batches. That
              money is committed too, and the selection screen's gate already counts it,
              so leaving it out would have this panel and that screen disagree about how
              much the owner has left.
            -->
            <span v-if="Number(o.other_draft_total) > 0" class="text-gray-400">
              (incl. other drafts)
            </span>
          </div>
          <!--
            The number that decides whether this batch can go. Negative means the batch
            takes more than the owner has, and the amounts have to come down before it is
            posted - which is what this screen is for.
          -->
          <div
            class="font-semibold"
            :class="Number(o.balance_after_posting) < 0 ? 'text-red-600' : ''"
          >
            {{ money(o.balance_after_posting) }}
          </div>
        </div>
      </div>

      <div v-if="Number(o.other_draft_total) > 0" class="mt-2 text-gray-600">
        This owner also has {{ money(o.other_draft_total) }} in
        {{ o.other_draft_lines }} line(s) on other draft batches, already subtracted above.
      </div>

      <div v-if="Number(o.balance_after_posting) < 0" class="mt-2 text-red-600">
        This batch is {{ money(Math.abs(Number(o.balance_after_posting))) }} more than the
        owner has. Reduce the amounts before posting.
      </div>

      <!--
        A DATA FAULT, not a normal state. One owner is one party: a party_identification
        row with role Owner whose id_value is that owner's primary key. Where several
        owner accounts point at ONE party - an import artifact - the ledger, which keys on
        party, cannot tell their money apart, and all of them show this same balance. It
        is flagged rather than explained away, because the fix is in the data.
      -->
      <div v-if="o.balance_is_shared" class="mt-2 text-amber-700">
        Data problem: {{ o.accounts_on_party }} owner accounts share one party
        identification, so this balance cannot be attributed to one of them and
        {{ money(o.owner_balance) }} shows against all of them. Each owner should have its
        own party. Have this corrected before relying on the figure.
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
const value = defineModel<any>("value");
const form = defineModel<any>("form", {
  default: () => ({}),
});
const props = defineProps<{
  data: any;
}>();

// TemplateQuerySet returns results keyed by the query set description.
const owners = computed(() => {
  const rows = props.data?.["Owner Balance"];
  return Array.isArray(rows) ? rows : [];
});

function money(v: any) {
  if (v === null || v === undefined) return "-";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(Number(v));
}
</script>

<style scoped></style>
